import React, { useState, useEffect, useRef } from 'react';
import { Col, Row, Card, Form, Button } from 'react-bootstrap';
import {
  Organization,
  PopupsForAddingState,
  PopupsState,
  ReturnedPortfolio,
} from '../Create-Portfolio/types';
import '../Preview/Preview.css';
import { maxDate, minDate } from '../../config/calendarMinMaxDates';
import {
  areObjectsEqual,
  formatDate,
  handleInputChange,
  handleNewItemFields,
} from '../UserProfile/helpers';
import { newOrganization } from '../Create-Portfolio/initialValues';
import { toast } from 'react-toastify';
import { refreshPortfolioAfterSave, type PortfolioGetDataFn } from '../../utils/portfolioRefresh';
import axiosInstance from '../../api/api';
import { API_ROUTES } from '../../config/api-routes';

interface Props {
  index: number;
  portfolio: ReturnedPortfolio;
  portfolioId: number;
  setOpenedPopups: React.Dispatch<React.SetStateAction<PopupsState>>;
  setPopupsToAdd: React.Dispatch<React.SetStateAction<PopupsForAddingState>>;
  setPortfolioData: React.Dispatch<React.SetStateAction<ReturnedPortfolio>>;
  getData?: PortfolioGetDataFn;
  popupVariant: string;
}

const OrganizationsPopup: React.FC<Props> = ({
  index,
  portfolio,
  portfolioId,
  setOpenedPopups,
  setPopupsToAdd,
  setPortfolioData,
  getData,
  popupVariant,
}) => {
  const [organizations, setOrganizations] = useState(portfolio.organizations);
  const [organization, setNewOrganization] = useState<any>(newOrganization);
  const [isCurrent, setIsCurrent] = useState<boolean>(
    portfolio.organizations[index]?.endDate === null
  );
  const [isNewSwitch, setNewSwitch] = useState<boolean>(false);
  const [startDate, setStartDate] = useState(portfolio.organizations[index]?.startDate || '');

  const popupRef = useRef<HTMLDivElement>(null);
  const normalizeForAPI = (v: string | undefined) => (v && v.trim() ? v : null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        // setPopupsToAdd((prev: PopupsForAddingState) => ({
        //   ...prev,
        //   organizations: false,
        // }));
        // setOpenedPopups((prev) => {
        //   const newOrganizationsPopups = [...prev.projects];
        //   newOrganizationsPopups[index] = false;
        //   return {
        //     ...prev,
        //     organizations: newOrganizationsPopups,
        //   };
        // });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popupRef, setOpenedPopups, setPopupsToAdd, index]);

  useEffect(() => {
    setOrganizations(portfolio.organizations);
  }, [portfolio.organizations]);

  useEffect(() => {
    setIsCurrent(portfolio.organizations[index]?.endDate === null);
  }, [index, portfolio.organizations]);

  useEffect(() => {
    if (isNewSwitch) {
      setNewOrganization((prev: Organization) => ({
        ...prev,
        endDate: '',
      }));
    }
  }, [isNewSwitch]);

  const saveOrganizations = async (updatedOrganizations: Organization[]) => {
    if (!portfolioId) return;
    try {
      const response = await axiosInstance.post(
        `${API_ROUTES.PORTFOLIO.EDIT_PORTFOLIO}/${portfolioId}`,
        { organizations: updatedOrganizations }
      );
      toast.success('Organization saved.');
      await refreshPortfolioAfterSave(setPortfolioData, response.data?.portfolio, getData);
    } catch (error) {
      console.error('Failed to save organization:', error);
      toast.error('We could not save this organization. Please check the details and try again.');
    }
  };

  const handleOrganizationSubmit = (index: number) => {
    if (organizations.length === undefined) return;
    if (areObjectsEqual(organizations[index], portfolio.organizations[index])) return;

    const outgoing = organizations.map((o: any) => ({
      ...o,
      endDate: normalizeForAPI(o.endDate as string),
    }));

    setPortfolioData((prev) => ({ ...prev, organizations: outgoing }));
    saveOrganizations(outgoing);

    setOpenedPopups((prev) => {
      const newOrganizationsPopups = [...prev.projects];
      newOrganizationsPopups[index] = false;
      return { ...prev, organizations: newOrganizationsPopups };
    });
  };

  const handleSubmitOfNewOrganization = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const adjusted = {
      ...organization,
      endDate: normalizeForAPI(organization.endDate as string),
    };

    const updatedOrganizations = [...organizations, adjusted];
    setOrganizations(updatedOrganizations);
    setPortfolioData((prev) => ({
      ...prev,
      organizations: updatedOrganizations,
    }));

    setNewOrganization(newOrganization);
    saveOrganizations(updatedOrganizations);
    setPopupsToAdd((prev) => ({ ...prev, organizations: false }));
  };

  const handleSwitch = () => {
    const updatedOrganizations = [...organizations];
    updatedOrganizations[index].endDate = isCurrent ? '' : '';
    setOrganizations(updatedOrganizations);
    setIsCurrent(!isCurrent);
  };

  const handleNewSwitch = () => {
    setNewSwitch((prev) => !prev);
  };

  const handleStartDateChange = (index: any, value: any) => {
    handleInputChange(index, 'startDate', value, organizations, setOrganizations);
    setStartDate(value);
  };

  const handleNewStartDateChange = (value: any) => {
    handleNewItemFields('startDate', value, setNewOrganization);
    setStartDate(value);
  };

  return (
    <Card
      className={`${popupVariant === 'adding' ? '' : 'user-popup'} p-3 popup-card`}
      ref={popupRef}
    >
      <Card.Body className="scrollable-card">
        {popupVariant !== 'adding' && (
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              handleOrganizationSubmit(index);
            }}
          >
            {/* Organization Name */}
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={12}>
                Name of organization
              </Form.Label>
              <Col sm={12}>
                <Form.Control
                  required
                  type="text"
                  defaultValue={portfolio.organizations[index].name}
                  onChange={(e) =>
                    handleInputChange(
                      index,
                      'name',
                      e.target.value,
                      organizations,
                      setOrganizations
                    )
                  }
                />
              </Col>
            </Form.Group>

            {/* Start and End Dates */}
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={12}>
                Start Date
              </Form.Label>
              <Col sm={12}>
                <Form.Control
                  required
                  type="date"
                  className="form-control"
                  defaultValue={formatDate(portfolio.organizations[index].startDate)}
                  onChange={(e) => handleStartDateChange(index, e.target.value)}
                  min={minDate}
                  max={maxDate}
                />
              </Col>

              <Form.Label column sm={12}>
                End Date
              </Form.Label>
              {!isCurrent && (
                <Col sm={12}>
                  <Form.Control
                    required
                    type="date"
                    className="form-control"
                    defaultValue={formatDate(portfolio.organizations[index].endDate)}
                    onChange={(e) =>
                      handleInputChange(
                        index,
                        'endDate',
                        e.target.value,
                        organizations,
                        setOrganizations
                      )
                    }
                    min={startDate || minDate}
                    max={maxDate}
                  />
                </Col>
              )}

              <Col sm={12}>
                <Form.Check
                  type="switch"
                  label="Current"
                  checked={isCurrent}
                  onChange={handleSwitch}
                  value="Current"
                />
              </Col>
            </Form.Group>

            {/* Description */}
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={12}>
                Brief description
              </Form.Label>
              <Col sm={12}>
                <Form.Control
                  required
                  as="textarea"
                  max={50}
                  rows={6}
                  defaultValue={portfolio.organizations[index].description}
                  onChange={(e) =>
                    handleInputChange(
                      index,
                      'description',
                      e.target.value,
                      organizations,
                      setOrganizations
                    )
                  }
                />
              </Col>
            </Form.Group>

            {/* Organization Link */}
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={12}>
                Organization link
              </Form.Label>
              <Col sm={12}>
                <Form.Control
                  required
                  type="text"
                  defaultValue={portfolio.organizations[index].link}
                  onChange={(e) =>
                    handleInputChange(
                      index,
                      'link',
                      e.target.value,
                      organizations,
                      setOrganizations
                    )
                  }
                />
              </Col>
            </Form.Group>

            {/* Submit Button */}
            <Button variant="success" type="submit">
              Submit
            </Button>
          </Form>
        )}

        {/* Adding a New Organization */}
        {popupVariant === 'adding' && (
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmitOfNewOrganization(e);
            }}
          >
            <Card.Title className="mb-3">Add organization</Card.Title>

            {/* Organization Name */}
            <Form.Group as={Row} className="mb-3">
              <Col sm={12}>
                <Form.Control
                  required
                  placeholder="Enter a name"
                  type="text"
                  onChange={(e) => handleNewItemFields('name', e.target.value, setNewOrganization)}
                />
              </Col>
            </Form.Group>

            {/* Start and End Dates */}
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={12}>
                Start Date
              </Form.Label>
              <Col sm={12}>
                <Form.Control
                  required
                  type="date"
                  className="form-control"
                  onChange={(e) => handleNewStartDateChange(e.target.value)}
                  min={minDate}
                  max={maxDate}
                />
              </Col>

              <Form.Label column sm={12}>
                End Date
              </Form.Label>
              {!isNewSwitch && (
                <Col sm={12}>
                  <Form.Control
                    required
                    type="date"
                    className="form-control"
                    onChange={(e) =>
                      handleNewItemFields('endDate', e.target.value, setNewOrganization)
                    }
                    min={startDate || minDate}
                    max={maxDate}
                  />
                </Col>
              )}

              <Col sm={12}>
                <Form.Check
                  type="switch"
                  label="Current"
                  value="Current"
                  checked={isNewSwitch}
                  onChange={handleNewSwitch}
                />
              </Col>
            </Form.Group>

            {/* Description */}
            <Form.Group as={Row} className="mb-3">
              <Col sm={12}>
                <Form.Control
                  required
                  placeholder="Enter a description"
                  as="textarea"
                  max={50}
                  rows={3}
                  onChange={(e) =>
                    handleNewItemFields('description', e.target.value, setNewOrganization)
                  }
                />
              </Col>
            </Form.Group>

            {/* Organization Link */}
            <Form.Group as={Row} className="mb-3">
              <Col sm={12}>
                <Form.Control
                  required
                  type="text"
                  placeholder="Enter a link"
                  onChange={(e) => handleNewItemFields('link', e.target.value, setNewOrganization)}
                />
              </Col>
            </Form.Group>

            {/* Submit Button */}
            <Button variant="success" type="submit">
              Submit
            </Button>
          </Form>
        )}
      </Card.Body>
    </Card>
  );
};

export default OrganizationsPopup;
